// ─────────────────────────────────────────
// ts/theme.ts — Tema claro/escuro
// Resolução: escolha salva → preferência do sistema → claro (padrão).
// O <script> inline do <head> já grava data-theme antes do primeiro
// paint; aqui ficam o listener do sistema, o botão e a persistência.
// Tauvior — Hardware, sistemas embarcados e conectividade industrial
// ─────────────────────────────────────────

import { getTranslation } from '../i18n/index';

export type Theme = 'light' | 'dark';

/** Mesma chave e mesma regra do script inline dos HTMLs — manter em sincronia. */
const STORAGE_KEY = 'tauvior-theme';
const DEFAULT_THEME: Theme = 'light';
const THEME_EVENT = 'tauvior:themechange';

/** theme-color da barra do navegador: a superfície de cada tema. */
const THEME_COLOR: Record<Theme, string> = {
  light: '#f2f2f2',
  dark: '#1e2125',
};

const TOGGLE_CLASS = 'theme-toggle';

const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');

// ── Persistência (storage pode lançar: modo privado, cookies bloqueados) ──

function readSaved(): Theme | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function save(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* sem storage: a escolha vale só nesta visita */
  }
}

// ── Resolução ──────────────────────────────

function systemTheme(): Theme {
  return document.body.dataset.page === 'home' ? DEFAULT_THEME : (darkQuery.matches ? 'dark' : DEFAULT_THEME);
}

export function resolveTheme(): Theme {
  return readSaved() ?? systemTheme();
}

export function getTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

// ── Aplicação ──────────────────────────────

function updateThemeColor(theme: Theme): void {
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => {
      meta.content = THEME_COLOR[theme];
    });
}

function applyTheme(theme: Theme): void {
  const previous = document.documentElement.dataset.theme;
  document.documentElement.dataset.theme = theme;
  updateThemeColor(theme);
  syncToggles();
  if (previous !== theme) {
    document.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { theme } }));
  }
}

// ── Botão ──────────────────────────────────

/**
 * Traço de linha, mesma família dos ícones do site (24×24, stroke
 * currentColor). Os dois ficam no DOM; o CSS mostra o certo por tema.
 */
const SUN_ICON = `<svg class="theme-toggle-sun" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.6"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6 7 7M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
const MOON_ICON = `<svg class="theme-toggle-moon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M20 14.2A8 8 0 0 1 9.8 4a8 8 0 1 0 10.2 10.2Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"/></svg>`;

/**
 * O rótulo é fixo ("Tema escuro") e o estado vai em aria-pressed — trocar o
 * rótulo junto com o estado faria o leitor de tela anunciar duas vezes. O
 * `title` diz a ação que o clique faz.
 */
function syncToggles(): void {
  const dark = getTheme() === 'dark';
  let label = 'Dark theme';
  let action = dark ? 'Switch to light theme' : 'Switch to dark theme';
  try {
    const { theme } = getTranslation().nav;
    label = theme.label;
    action = dark ? theme.toLight : theme.toDark;
  } catch {
    /* tradução ainda não carregada: o próximo sync corrige */
  }
  document.querySelectorAll<HTMLButtonElement>(`.${TOGGLE_CLASS}`).forEach((btn) => {
    btn.setAttribute('aria-label', label);
    btn.setAttribute('aria-pressed', String(dark));
    btn.title = action;
  });
}

export function toggleTheme(): void {
  const next: Theme = getTheme() === 'dark' ? 'light' : 'dark';
  save(next);
  applyTheme(next);
}

/**
 * Cria o botão dentro de `container` (antes de `before`, se dado).
 * Idempotente: um segundo chamado na mesma página não duplica o botão.
 */
export function mountThemeToggle(container: HTMLElement, before?: Element | null): void {
  if (container.querySelector(`.${TOGGLE_CLASS}`)) return;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = TOGGLE_CLASS;
  btn.innerHTML = MOON_ICON + SUN_ICON;
  btn.addEventListener('click', toggleTheme);
  container.insertBefore(btn, before ?? null);
  syncToggles();
}

// ── Init ───────────────────────────────────

let initialised = false;

export function initTheme(): void {
  if (initialised) return;
  initialised = true;

  applyTheme(resolveTheme());

  // Sem escolha salva, o site acompanha o sistema ao vivo.
  darkQuery.addEventListener('change', () => {
    if (readSaved() === null) applyTheme(systemTheme());
  });

  // Outra aba mudou a escolha.
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) applyTheme(resolveTheme());
  });
}
